import React, { useState, useEffect } from 'react';
import {
    DndContext,
    DragOverlay,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    // arrayMove,
    // SortableContext,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { IoInformationCircleOutline } from 'react-icons/io5';
import VehicleDetails, { VehiclePosition } from './tire-component/vehicle-details';
import Shop from './tire-component/shop';
import UnAssigned from './tire-component/un-assigned';
import DraggableTire from './tire-component/DraggableTire';
// import { initializeServiceBay } from './tire-component/tire-data';
// import DragInstructions from './tire-component/DragInstructions';
import Loader from '../../../../components/shared/loader';
import { updateTire } from '../../../../services/tires-service';
import Modal from '../../../../components/shared/modal';
import PositionViewModal from './tire-component/position-view-modal';

const Tires = ({ vehicle, vehicleId, accountId, data, setData, tirePositions, setTirePositions, tireUnAssign, loading, error, setTireUnAssign, globalFilter, onGlobalFilterChange, fetchTires }) => {
    const [positions, setPositions] = useState(tirePositions);
    const [activeId, setActiveId] = useState(null);
    const [activeTire, setActiveTire] = useState(null);
    const [notification, setNotification] = useState(null);
    const [assignedPositionedTires, setAssignedPositionedTires] = useState([]);
    const [assignedTires, setAssignedTires] = useState([]);
    const [openPositionModal, setOpenPositionModal] = useState(false);
    const [isOverVehicle, setIsOverVehicle] = useState(false);

    const handleDragOver = ({ over }) => {
        setIsOverVehicle(over?.data?.current?.sectionType === 'vehicle');
    };

    const handleOpenPostionModal = () => {
        setOpenPositionModal(!openPositionModal)
    }


    useEffect(() => {
        if (data && tirePositions) {
            const withPosition = data.filter(tire => tire.position !== null);
            const withoutPosition = data.filter(tire => tire.position === null);
            setAssignedPositionedTires(withPosition);
            setAssignedTires(withoutPosition);

            // Transform tirePositions into a vehicle map
            const vehicleMap = {};
            tirePositions.forEach(pos => {
                vehicleMap[pos.slug] = {
                    id: pos.slug,
                    label: pos.label,
                    tire: withPosition.find(tire => tire.position === pos.slug) || null
                };
            });

            setPositions(prev => ({
                ...prev,
                vehicle: vehicleMap
            }));
        }
    }, [data, tirePositions]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleDragStart = (event) => {
        const { active } = event;
        setActiveId(active.id);

        let sourcePosition = null;
        let sourceSection = null;
        let sourceTire = null;

        // Check if it's an unassigned tire from section 3
        if (active.id.startsWith('unassigned-')) {
            const tireId = parseInt(active.id.replace('unassigned-', ''));
            sourceTire = tireUnAssign.find(tire => tire.tireId === tireId);
            sourceSection = 'serviceBay';
        }
        // Check if it's a shop tire (from section 2)
        else if (active.id.startsWith('shop-tire-')) {
            const tireId = parseInt(active.id.replace('shop-tire-', ''));
            sourceTire = assignedTires.find(tire => tire.tireId === tireId);
            sourceSection = 'shop';
        }
        // Check vehicle positions (section 1)
        else if (positions.vehicle[active.id]?.tire) {
            sourcePosition = positions.vehicle[active.id];
            sourceSection = 'vehicle';
            sourceTire = sourcePosition.tire;
        }

        if (sourceTire) {
            setActiveTire(sourceTire);
        }
    };

    const handleDragEnd = async (event) => {
        setIsOverVehicle(false);
        const { active, over } = event;

        if (active.id !== over?.id && over?.id) {
            let sourceTire = null;
            let sourceSection = null;
            let targetSection = null;
            let targetPosition = null;

            // Handle source identification
            if (active.id.startsWith('unassigned-')) {
                const tireId = parseInt(active.id.replace('unassigned-', ''));
                sourceTire = tireUnAssign.find(tire => tire.tireId === tireId);
                sourceSection = 'serviceBay';
            } else if (active.id.startsWith('shop-tire-')) {
                const tireId = parseInt(active.id.replace('shop-tire-', ''));
                sourceTire = assignedTires.find(tire => tire.tireId === tireId);
                sourceSection = 'shop';
            } else if (positions.vehicle[active.id]?.tire) {
                sourceTire = positions.vehicle[active.id].tire;
                sourceSection = 'vehicle';
                targetPosition = active.id;
            }

            // Handle target identification
            if (over.id === 'shop') {
                targetSection = 'shop';
            } else if (over.id === 'serviceBay') {
                targetSection = 'serviceBay';
            } else if (positions.vehicle[over.id]) {
                targetSection = 'vehicle';
                targetPosition = over.id;
            }

            if (sourceTire && targetSection) {
                console.log(sourceTire)
                if (!isMoveAllowed(sourceSection, targetSection)) {
                    showNotification(`Cannot move tire from ${sourceSection} to ${targetSection}`, 'error');
                    setActiveId(null);
                    setActiveTire(null);
                    return;
                }

                try {
                    // Section 3 → Section 2 (Unassigned to Shop)
                    if (sourceSection === 'serviceBay' && targetSection === 'shop') {
                        setTireUnAssign(prev => prev.filter(t => t.tireId !== sourceTire.tireId));
                        const updatedTire = {
                            ...sourceTire,
                            id: `shop-tire-${sourceTire.tireId}`
                        };
                        setAssignedTires(prev => [...prev, updatedTire]);
                        showNotification(`Moved ${sourceTire.brand} to Shop`, 'success');

                        const response = await updateTire(accountId, sourceTire.tireId, {
                            vehicleId: vehicleId,
                        });

                        if (!response.success) {
                            throw new Error(response.data.message || "Update failed");
                        }
                    }
                    // Section 2 → Section 3 (Shop to Unassigned)
                    else if (sourceSection === 'shop' && targetSection === 'serviceBay') {
                        setAssignedTires(prev => prev.filter(t => t.tireId !== sourceTire.tireId));
                        const updatedTire = {
                            ...sourceTire,
                            vehicle: { vehicleId: 0, name: null },
                            id: `unassigned-${sourceTire.tireId}`
                        };
                        setTireUnAssign(prev => [...prev, updatedTire]);
                        showNotification(`Moved ${sourceTire.brand} to Unassigned`, 'success');

                        const response = await updateTire(accountId, sourceTire.tireId, {
                            vehicleId: 0,
                        });

                        if (!response.success) {
                            throw new Error(response.data.message || "Update failed");
                        }
                    }
                    // Section 2 → Section 1 (Shop to Vehicle position)
                    else if (sourceSection === 'shop' && targetSection === 'vehicle') {
                        // Optimistic UI update for assignedPositionedTires
                        const updatedTire = {
                            ...sourceTire,
                            position: targetPosition,
                            vehicle: { vehicleId: vehicleId, name: null }
                        };
                        setAssignedTires(prev => prev.filter(t => t.tireId !== sourceTire.tireId));
                        setAssignedPositionedTires(prev => [...prev, updatedTire]);
                        setPositions(prev => ({
                            ...prev,
                            vehicle: {
                                ...prev.vehicle,
                                [targetPosition]: {
                                    ...prev.vehicle[targetPosition],
                                    tire: sourceTire
                                }
                            }
                        }));
                        showNotification(`Moved ${sourceTire.brand} to Vehicle (${targetPosition})`, 'success');

                        // Async API call
                        const response = await updateTire(accountId, sourceTire.tireId, {
                            position: targetPosition,
                        });

                        console.log("Section 2 to 1 : ", response)

                        if (!response.success) {
                            throw new Error(response.data.message || "Update failed");
                        }
                    }
                    // Section 1 → Section 2 (Vehicle to Shop)
                    else if (sourceSection === 'vehicle' && targetSection === 'shop') {
                        const updatedTire = {
                            ...sourceTire,
                            position: null,
                            vehicle: { vehicleId: vehicleId, name: null }
                        };
                        setAssignedTires(prev => [...prev, updatedTire]);
                        setAssignedPositionedTires(prev => prev.filter(t => t.tireId !== sourceTire.tireId));
                        setPositions(prev => ({
                            ...prev,
                            vehicle: {
                                ...prev.vehicle,
                                [targetPosition]: {
                                    ...prev.vehicle[targetPosition],
                                    tire: null
                                }
                            }
                        }));
                        showNotification(`Moved ${sourceTire.brand} to Shop`, 'success');

                        const response = await updateTire(accountId, sourceTire.tireId, {
                            position: null,
                        });

                        console.log("Section 1 to 2 : ", response);

                        if (!response.success) {
                            throw new Error(response.data.message || "Update failed");
                        }
                    }

                    fetchTires();
                } catch (error) {
                    console.error("Error moving tire:", error);
                    showNotification(`Failed to move tire: ${error.message}`, 'error');

                    // Revert optimistic updates
                    if (sourceSection === 'serviceBay' && targetSection === 'shop') {
                        setAssignedTires(prev => prev.filter(t => t.tireId !== sourceTire.tireId));
                        setTireUnAssign(prev => [...prev, sourceTire]);
                    } else if (sourceSection === 'shop' && targetSection === 'serviceBay') {
                        setTireUnAssign(prev => prev.filter(t => t.tireId !== sourceTire.tireId));
                        setAssignedTires(prev => [...prev, sourceTire]);
                    } else if (sourceSection === 'shop' && targetSection === 'vehicle') {
                        setAssignedTires(prev => [...prev, sourceTire]);
                        setAssignedPositionedTires(prev => prev.filter(t => t.tireId !== sourceTire.tireId));
                        setPositions(prev => ({
                            ...prev,
                            vehicle: {
                                ...prev.vehicle,
                                [targetPosition]: {
                                    ...prev.vehicle[targetPosition],
                                    tire: null
                                }
                            }
                        }));
                    } else if (sourceSection === 'vehicle' && targetSection === 'shop') {
                        setAssignedTires(prev => prev.filter(t => t.tireId !== sourceTire.tireId));
                        setAssignedPositionedTires(prev => [...prev, sourceTire]);
                        setPositions(prev => ({
                            ...prev,
                            vehicle: {
                                ...prev.vehicle,
                                [targetPosition]: {
                                    ...prev.vehicle[targetPosition],
                                    tire: sourceTire
                                }
                            }
                        }));
                    }
                }
            }

            setActiveId(null);
            setActiveTire(null);
        }
    };

    const handleDragCancel = () => {
        setIsOverVehicle(false); // Reset on drag cancel
    };

    const isMoveAllowed = (fromSection, toSection) => {
        // Allowed moves:
        // Service Bay -> Vehicle (Section 3 -> Section 1)
        // Service Bay -> Shop (Section 3 -> Section 2)
        // Shop -> Vehicle (Section 2 -> Section 1)
        // Shop -> Service Bay (Section 2 -> Section 3)
        // Vehicle -> Shop (Section 1 -> Section 2)
        const allowedMoves = [
            ['serviceBay', 'vehicle'],
            ['serviceBay', 'shop'],
            ['shop', 'vehicle'],
            ['shop', 'serviceBay'],
            ['vehicle', 'shop']
        ];

        return allowedMoves.some(([from, to]) => from === fromSection && to === toSection);
    };

    const slugs = [ 'a1l', 'a1r', 'a2li', 'a2lo', 'a2ri', 'a2ro', 'a3li', 'a3lo', 'a3ri', 'a3ro', 'sp'];


    return (
        <div className="relative">
            {/* {notification && (
                <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${notification.type === 'success'
                    ? 'bg-green-500 text-white'
                    : 'bg-red-500 text-white'
                    }`}>
                    {notification.message}
                </div>
            )} */}

            {(error || notification?.type === 'error') && (
                <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                    {error || notification.message}
                </div>
            )}

            {
                loading ?
                    <Loader />
                    :
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDragCancel={handleDragCancel}
                    >
                        <div className='w-full grid md:grid-cols-3 grid-cols-1 xl:gap-10 lg:gap-6 gap-4 py-10 min-h-[50vh]'>
                            {/* Vehicle Details Section */}
                            <div className='w-full h-full border border-[#ebebeb] shadow-lg'>
                                <div className='py-3 flex justify-center items-center'>
                                    <h1 className='font-bold text-lg'>{vehicle?.name}</h1>
                                </div>
                                <div className='flex w-full justify-end' >
                                    <button onClick={handleOpenPostionModal} className='flex cursor-pointer items-center gap-1  px-6'>
                                        <div className=''>
                                            <IoInformationCircleOutline className='text-2xl' />
                                        </div>
                                        Position
                                    </button>
                                </div>

                                <VehicleDetails
                                    initialData={assignedPositionedTires}
                                    positions={tirePositions}
                                    onTireMove={(fromId, toId) => {
                                        // Handle tire movement within vehicle
                                    }}
                                />
                            </div>

                            {/* Service Bay Section */}
                            <div className='w-full h-full border border-[#ebebeb] shadow-lg'>
                                <div className='py-3 flex justify-center items-center'>
                                    <h1 className='font-bold text-lg'>Service Bay</h1>
                                </div>
                                <Shop
                                    initialData={assignedTires}
                                    onTireMove={(fromId, toId) => {
                                        // Handle tire movement within shop
                                    }}
                                />
                            </div>

                            {/* Shop Section */}
                            <div className='w-full h-full border border-[#ebebeb] shadow-lg'>
                                <div className='py-3 flex justify-center items-center'>
                                    <h1 className='font-bold text-lg'>Tires in Shop</h1>
                                </div>
                                <UnAssigned
                                    initialData={tireUnAssign}
                                    onTireMove={(fromId, toId) => {
                                        // Handle tire movement within service bay
                                    }}
                                />
                            </div>
                        </div>

                        {/* Drag Overlay */}
                        <DragOverlay dropAnimation={null}>
                            {activeId && activeTire ? (
                                slugs.includes(activeId) ? (
                                    <VehiclePosition activeId={activeId === 'sp' ? 'isSp' : ''} />
                                ) : (
                                    <div className={isOverVehicle ? "transform scale-75 w-40 origin-center" : ""}>
                                        <DraggableTire
                                            tire={activeTire}
                                            position={{ id: activeId, label: 'Dragging' }}
                                            isOverlay={true}
                                        />
                                    </div>
                                )
                            ) : null}
                        </DragOverlay>

                        <Modal
                            isOpen={openPositionModal}
                            onClose={handleOpenPostionModal}
                            modalClass={'xl:!w-[20%] lg:!w-[35%] md:!w-[40%] sm:!w-[45%] !w-[90%] !px-0'}
                        >
                            <PositionViewModal
                                positions={positions}
                                onClose={handleOpenPostionModal}
                            />

                        </Modal>
                    </DndContext>
            }
        </div>
    );
};

export default Tires;